import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { doc } from 'firebase/firestore'
import { setDoc } from '@/lib/firestore'
import { db } from '../lib/firebase'
import { log } from '../lib/logger'
import { toast } from 'sonner'
import type { User } from 'firebase/auth'
import { setAnalyticsUserProperty, track } from '../lib/analytics'
import type { AnalyticsPushEnabled } from '../lib/analytics'
import type { RequestAddressedType } from '@shared/notifications'
import type { SetChangeKind } from '@shared/setChange'

// The plugin object is a Capacitor proxy that answers *every* property access
// with a function — including `then`. Resolving it out of an async function
// makes JS treat it as a thenable and call `FirebaseMessaging.then()`, which
// throws "not implemented on android" and kills registration before any
// listener is attached. Keep it boxed so it never crosses an await bare.
async function getMessagingPlugin() {
  if (!Capacitor.isPluginAvailable('FirebaseMessaging')) return null
  const mod = await import('@capacitor-firebase/messaging')
  return { plugin: mod.FirebaseMessaging }
}

function pushEnabledLabel(receive: string): AnalyticsPushEnabled {
  return receive === 'granted' || receive === 'denied' ? receive : 'default'
}

// Re-read on resume because the user can flip the permission in OS settings
// without the app ever asking again.
function useReportPushPermission() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      setAnalyticsUserProperty('push_enabled', 'unsupported')
      return
    }
    const report = async () => {
      const boxed = await getMessagingPlugin()
      if (!boxed) return
      const { receive } = await boxed.plugin.checkPermissions()
      setAnalyticsUserProperty('push_enabled', pushEnabledLabel(receive))
    }
    const reportSafely = () => void report().catch(err => log('push', 'permission check failed', { error: String(err) }))
    reportSafely()
    const handle = App.addListener('resume', reportSafely)
    return () => void handle.then(h => h.remove())
  }, [])
}

function notificationData(data: unknown): Record<string, string> {
  return (data ?? {}) as Record<string, string>
}

// Module scope on purpose: the setup effect below declares its own local
// `track(handle)` for listener bookkeeping, which would shadow the analytics
// import if this lived inside it.
function trackOpen(data: Record<string, string>) {
  if (data.type === 'request_addressed' && data.requestType) {
    track('request_addressed_opened', { request_type: data.requestType as RequestAddressedType })
  }
  if (data.type === 'schedule_update' && data.changeKind) {
    track('schedule_update_opened', {
      kind: data.changeKind as SetChangeKind,
      set_count: Number(data.changeCount ?? 0),
    })
  }
}

export function usePushNotifications(user: User | null) {
  const navigate = useNavigate()
  // Held in a ref so a new navigate identity can't tear down the listeners.
  const navigateRef = useRef(navigate)
  useEffect(() => {
    navigateRef.current = navigate
  }, [navigate])

  const uid = user && !user.isAnonymous ? user.uid : null
  useReportPushPermission()

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    if (!uid) return

    let cancelled = false
    let cleanups: Array<() => void> = []

    // Listeners resolve asynchronously; if the effect was torn down while one
    // was in flight, drop it instead of leaking it past the cleanup.
    function track(handle: { remove: () => Promise<void> }) {
      if (cancelled) void handle.remove()
      else cleanups.push(() => void handle.remove())
    }

    async function saveToken(token: string) {
      log('push', 'registered', { token: token.slice(0, 12) + '…' })
      try {
        const platform = Capacitor.getPlatform() as 'ios' | 'android'
        const tokenRef = doc(db, `users/${uid}/private/fcmTokens`)
        // Nested map, not a `tokens.<token>` key — setDoc treats dots as
        // literal field-name characters, only updateDoc splits them.
        await setDoc(tokenRef, {
          tokens: {
            [token]: {
              platform,
              createdAt: new Date(),
              lastUsedAt: new Date(),
            },
          },
        }, { merge: true })
      } catch (err) {
        log('push', 'token save failed', { error: String(err) })
      }
    }

    async function setup() {
      const boxed = await getMessagingPlugin()
      if (!boxed) return
      const FirebaseMessaging = boxed.plugin

      const permission = await FirebaseMessaging.requestPermissions()
      setAnalyticsUserProperty('push_enabled', pushEnabledLabel(permission.receive))
      if (permission.receive !== 'granted') {
        log('push', 'permission denied')
        return
      }

      // Refreshed tokens arrive here rather than from getToken().
      const tokenListener = await FirebaseMessaging.addListener(
        'tokenReceived',
        ({ token }) => void saveToken(token),
      )
      track(tokenListener)

      // Foreground notification — show a toast
      const fgListener = await FirebaseMessaging.addListener(
        'notificationReceived',
        ({ notification }) => {
          const data = notificationData(notification.data)
          const deepLink = data.deepLink
          toast(notification.title ?? 'Faro', {
            description: notification.body,
            action: deepLink ? {
              label: 'View',
              onClick: () => {
                trackOpen(data)
                navigateRef.current(deepLink)
              },
            } : undefined,
          })
        },
      )
      track(fgListener)

      // Background/killed tap — navigate to deep link
      const bgListener = await FirebaseMessaging.addListener(
        'notificationActionPerformed',
        ({ notification }) => {
          const data = notificationData(notification.data)
          const deepLink = data.deepLink
          if (deepLink) {
            trackOpen(data)
            navigateRef.current(deepLink)
          }
        },
      )
      track(bgListener)

      if (cancelled) return
      // On iOS this both registers with APNs and exchanges the device token
      // for an FCM one — the raw APNs token FCM would reject never surfaces.
      const { token } = await FirebaseMessaging.getToken()
      if (cancelled) return
      await saveToken(token)
    }

    void setup().catch(err => log('push', 'setup failed', { error: String(err) }))

    return () => {
      cancelled = true
      for (const cleanup of cleanups) cleanup()
      cleanups = []
    }
  }, [uid])
}
